//services
import API from 'services/api.services';
//constants
import {
	API_CREATE_PAYMENT,
	API_GET_BENEFICIARY_PAYMENTS_HISTORY,
	API_GET_PAYMENT_BY_ID,
	API_GET_PAYMENT_FEES,
	API_GET_REFUND_BY_ID,
	API_AVAILABLE_PAYMENT_DAYS,
	API_REJECT_PAYMENT,
	API_SIGN_PAYMENT,
	API_UPLOAD_PAYMENT_FILE,
	API_VALIDATE_PAYMENT,
} from 'constants/db.constants';
import { intialMessage, userMesaageType } from 'constants/ui.constants';
import Event from '@mui/icons-material/Event';

//reducers
import {
	setActiveStep,
	setPaymentDates,
	setLoading,
	setMessage,
	setPayementFees,
	setSteps,
	upsertPay,
	setPaymentFeesLoading,
	setPaymentFeesError,
} from 'state/reducers/pay.reducers';
import _ from 'lodash';
import { CcBeneficiaryAddressErrorKeys, FileType, PayEnum, PaymentStatus, paySteps } from 'constants/pay.constants';
import { setSeconds } from 'date-fns';
import { getBalancesByCompanyId } from './balance.services';
import { upsertBeneficiary } from 'state/reducers/beneficiary.reducers';
import { setAwaitingAuthorizationCategories, setOpenPendingCategories } from 'state/reducers/requests.reducers';
import { PendingRequestTypes } from 'constants/requests.constants';
import { setErrorFieldsMessage, setErrorMessage } from 'utils';

export function getFeesByCompanyId(companyId?: number | string, currencyId?: number | string) {
	return async (dispatch: any) => {
		dispatch(setPaymentFeesLoading(true));
		dispatch(setPaymentFeesError(false));
		dispatch(setMessage(intialMessage));
		try {
			let res = await API.getInstance().post(API_GET_PAYMENT_FEES, {
				companyId: companyId,
				currencyId: currencyId,
			});
			if (res?.data?.paymentFees) {
				dispatch(setPayementFees(res.data.paymentFees));
			}
			dispatch(setPaymentFeesLoading(false));
		} catch (e: any) {
			console.log(e);
			dispatch(setPaymentFeesLoading(false));
			dispatch(setPaymentFeesError(true));
			setErrorFieldsMessage(setMessage, e);
			if (_.includes([404, 500], _.get(e, 'statusCode'))) {
				setErrorMessage(setMessage, e);
			}
		}
	};
}

function setBeneficiaryAddressErrorMessage(e: any) {
	const errorKeysByPriority = [CcBeneficiaryAddressErrorKeys.country, CcBeneficiaryAddressErrorKeys.city];
	const errorKey = _.find(errorKeysByPriority, (key) => _.has(e?.data?.error, key));
	if (!errorKey) {
		return false;
	}

	_.assign(e.data, { error: { 'validations.errors.not_match': `page.pay.create.step.3.${errorKey}.text` } });
	setErrorMessage(setMessage, e);
	return true;
}

export function validatePayment(pay: object) {
	return async (dispatch: any) => {
		dispatch(setLoading(true));
		dispatch(setMessage(intialMessage));
		await API.getInstance()
			.post(API_VALIDATE_PAYMENT, pay)
			.then(() => {
				dispatch(setLoading(false));
				dispatch(setActiveStep(paySteps.REVIEW));
			})
			.catch((e) => {
				dispatch(setLoading(false));
				if (setBeneficiaryAddressErrorMessage(e)) {
					return;
				}
				let objError = {};
				if (e?.data?.errors) {
					_.keys(e?.data?.errors).forEach((key) => {
						objError['forms.beneficiary.field.' + key] = 'error.code.invalid_format';
					});
				} else setErrorMessage(setMessage, e);
			});
	};
}

export function createPayment(pay: object) {
	return async (dispatch: any) => {
		dispatch(setLoading(true));
		dispatch(setMessage(intialMessage));
		try {
			let res = await API.getInstance().post(API_CREATE_PAYMENT, pay);
			dispatch(
				upsertPay(
					_.pick(res?.data?.paymentFinal, [
						String(PayEnum.createdAt),
						String(PayEnum.dueDate),
						String(PayEnum.status),
					])
				)
			);
			dispatch(setLoading(false));
			dispatch(setActiveStep(paySteps.COMPLETE));
			dispatch(getBalancesByCompanyId());
		} catch (e: any) {
			dispatch(setLoading(false));
			if (setBeneficiaryAddressErrorMessage(e)) {
				return;
			}
			let objError = {};
			if (e?.data?.error) {
				_.keys(e?.data?.error).forEach((key) => {
					const code = key === 'purpose_code' ? 'reason_content' : key;
					objError['validations.errors.not_match'] = `page.pay.create.step.3.${code}.text`;
				});
				_.assign(e.data, { error: objError });
				setErrorMessage(setMessage, e);
			} else setErrorMessage(setMessage, e);
		}
	};
}

export function loadBeneficiariesPayments(id: number) {
	return async (dispatch: any) => {
		dispatch(setLoading(true));
		dispatch(setMessage(intialMessage));
		try {
			let res = await API.getInstance().post(API_GET_BENEFICIARY_PAYMENTS_HISTORY, { beneficiaryId: id });
			dispatch(
				upsertBeneficiary({
					paymentsList: res?.data?.payments,
				})
			);
			dispatch(setLoading(false));
		} catch (e: any) {
			dispatch(setLoading(false));
			setErrorMessage(setMessage, e);
		}
	};
}

export function getPaymentById(id: string) {
	return async (dispatch: any) => {
		dispatch(setLoading(true));
		dispatch(setMessage(intialMessage));
		try {
			let res = await API.getInstance().post(API_GET_PAYMENT_BY_ID, { paymentId: id });
			dispatch(setLoading(false));
			return res?.data?.payment;
		} catch (e: any) {
			dispatch(setLoading(false));
			setErrorMessage(setMessage, e);
		}
	};
}

export function getRefundById(id: string) {
	return async (dispatch: any) => {
		dispatch(setLoading(true));
		dispatch(setMessage(intialMessage));
		try {
			let res = await API.getInstance().get(API_GET_REFUND_BY_ID, { id });
			dispatch(setLoading(false));
			return res?.data?.refund;
		} catch (e: any) {
			dispatch(setLoading(false));
			setErrorMessage(setMessage, e);
		}
	};
}

export function getPaymentDates(currencyId: string, paymentDate: string) {
	return async (dispatch: any) => {
		dispatch(setLoading(true));
		dispatch(setMessage(intialMessage));
		const params = {
			currencyId: currencyId,
			paymentDate: paymentDate,
		};
		await API.getInstance()
			.post(API_AVAILABLE_PAYMENT_DAYS, params)
			.then((res) => {
				dispatch(setPaymentDates(res?.data?.validPaymentDates));
				dispatch(setLoading(false));
			})
			.catch((e) => {
				dispatch(setLoading(false));
				if (_.includes([400, 403, 404, 500], _.get(e, 'statusCode'))) {
					setErrorMessage(setMessage, e);
				}
			});
	};
}

export function changePaymentStatus(paymentId: number, status: PaymentStatus) {
	//TODO: change name validateStep1 to more clear
	let paramsSetting = {
		paymentId: paymentId,
	};
	const api = status === PaymentStatus.Approved ? API_SIGN_PAYMENT : API_REJECT_PAYMENT;
	return async (dispatch: any, getState: any) => {
		dispatch(setLoading(true));
		await API.getInstance()
			.post(api, paramsSetting)
			.then((response) => {
				let { pendingRequests } = getState();
				let _pendingRequests = { ...pendingRequests };
				dispatch(setLoading(false));
				try {
					let filter = _.filter(_pendingRequests.awaitingAuth[PendingRequestTypes.PAYMENTS], function (o) {
						return o.id !== paymentId;
					});
					dispatch(
						setAwaitingAuthorizationCategories({ list: filter, category: PendingRequestTypes.PAYMENTS })
					);
					if (status === PaymentStatus.Approved) {
						let find = _.filter(_pendingRequests.awaitingAuth[PendingRequestTypes.PAYMENTS], function (o) {
							return o.id === paymentId;
						});
						dispatch(
							setOpenPendingCategories({
								list: _.concat(_pendingRequests.openPending[PendingRequestTypes.PAYMENTS], find),
								category: PendingRequestTypes.PAYMENTS,
							})
						);
					}
				} catch (error) {
					console.log(error);
				}
			})
			.catch((error) => {
				console.log(error);
				dispatch(setLoading(false));
				if (setBeneficiaryAddressErrorMessage(error)) {
					return;
				}
				setErrorMessage(setMessage, error);
			});
	};
}

export function uploadPaymentFile(file: File, companyId: number) {
	return async (dispatch: any) => {
		const paramsSetting = new FormData();
		paramsSetting.append('companyId', String(companyId));
		paramsSetting.append('file', file, file.name);
		const result = await API.getInstance()
			.post(API_UPLOAD_PAYMENT_FILE, paramsSetting)
			.then((response) => {
				console.log(response?.data);
				return response.data;
			})
			.catch((error) => {
				setErrorMessage(setMessage, error);
			});
		return result;
	};
}
